/**
 * Titles of the records in the sheet stack, for the breadcrumb (§6.1: "Kofi A. /
 * GHS 250.00 withdrawal / Refund GHS 252.50"). Only the top sheet is mounted, so
 * each RecordSheet files its title here; the breadcrumb reads the lower ones
 * from this cache and falls back to the record's noun until one has loaded.
 *
 * The snapshot is an immutable object replaced on every change, so
 * `useSyncExternalStore` and the React Compiler both see a stable value.
 */
import { useSyncExternalStore } from 'react';
import { type SheetRef, sheetKey } from '@/lib/sheet-url';

const MAX_TITLES = 200;

type Snapshot = Readonly<Record<string, string>>;

let snapshot: Snapshot = {};
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): Snapshot {
  return snapshot;
}

/** Files the human title of a record the viewer has opened. */
export function rememberSheetTitle(ref: SheetRef, title: string): void {
  const key = sheetKey(ref);
  if (!title || snapshot[key] === title) return;
  const next: Record<string, string> = { ...snapshot };
  // Re-insert so the newest entries sit last, then drop the oldest past the cap.
  delete next[key];
  next[key] = title;
  const keys = Object.keys(next);
  for (const old of keys.slice(0, Math.max(0, keys.length - MAX_TITLES)))
    delete next[old];
  snapshot = next;
  for (const listener of listeners) listener();
}

/** The remembered titles, by `type:id`. Re-renders when one is added. */
export function useSheetTitles(): Snapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
